'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase' // Your existing client
import {
  AUTH_REDIRECT_STORAGE_KEY,
  getLocaleFromPathname,
  normalizeAuthRedirectTarget
} from '@/lib/auth-redirect'

type AuthContextType = {
  session: Session | null
  user: User | null
  isLoading: boolean
  signOut: () => Promise<void>
  signInWithGoogle: (redirectPath?: string | null) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // 1. Check active session immediately (From LocalStorage)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
    })

    // 2. Listen for changes (Sign in, Sign out, Token Refresh)
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    await supabase.auth.signOut()
    // Local state updates automatically via onAuthStateChange
  }

  const signInWithGoogle = async (redirectPath?: string | null) => {
    // Extract current locale from URL path (e.g., /en/login -> en)
    const locale = getLocaleFromPathname(window.location.pathname)
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const authRedirect = normalizeAuthRedirectTarget(
      redirectPath ?? currentPath,
      locale,
      window.location.origin
    )

    window.localStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, authRedirect)

    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        // Redirect to auth callback page with locale preserved
        redirectTo: `${window.location.origin}/${locale}/auth/callback`
      }
    })
  }

  return (
    <AuthContext.Provider
      value={{ session, user, isLoading, signOut, signInWithGoogle }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
